import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CHECK_PLATFORM_INTEGRATION,
  GET_PLATFORM_INTEGRATION,
  GET_PLATFORM_INTEGRATIONS,
  GET_PLATFORM_INTEGRATION_TEMPLATES,
  REFRESH_ISSUER_CARD_PRODUCTS,
  SAVE_PLATFORM_INTEGRATION,
  UPDATE_ISSUER_CARD_PRODUCT,
} from "graphql/platformIntegration";
import { GET_PLATFORM_REWARDS_AVAILABLE } from "graphql/platform";
import ApplicationType from "enums/ApplicationType";
import CardHolderType from "enums/CardHolderType";
import CardProgramStatus from "enums/CardProgramStatus";
import PlatformIntegrationCategory from "enums/PlatformIntegrationCategory";
import PlatformIntegrationFieldType from "enums/PlatformIntegrationFieldType";

export interface IssuerCardProduct {
  key: string;
  /** The issuer's own wording, which only a platform owner sees. */
  name?: string;
  /** What a customer is shown wherever this product is named. */
  displayName?: string;
  /** Read from the issuer, which decides whether the program lends or spends what it holds. */
  subAccountType?: ApplicationType;
  /** Who a company opening this program may issue cards to. The company picks one. */
  cardHolderTypes: CardHolderType[];
  /** Who the product can issue cards to now; an owner offers some of these. */
  supportedCardHolderTypes: CardHolderType[];
  enabled: boolean;
  /** Purchases on this program's cards earn rewards, and its companies see them. */
  rewards: boolean;
}

export interface IssuerCardProductUpdate {
  enabled?: boolean;
  displayName?: string;
  cardHolderTypes?: CardHolderType[];
  rewards?: boolean;
}

export interface PlatformIntegrationValue {
  key: string;
  value: string;
}

export interface PlatformIntegration {
  id: string;
  category: PlatformIntegrationCategory;
  provider: string;
  name: string;
  status?: CardProgramStatus;
  isDefault: boolean;
  /** Stored settings by template field. Secrets are never among them. */
  values: PlatformIntegrationValue[];
  /** The secret fields that hold a value. */
  storedSecrets: string[];
  /** Required fields that hold nothing. */
  missingFields: string[];
  /** Who a card issuing provider can issue cards to, from its capabilities. */
  supportedCardHolderTypes: CardHolderType[];
  createdAt?: number;
  updatedAt?: number;
  cardProducts: IssuerCardProduct[];
}

export interface PlatformIntegrationField {
  key: string;
  type: PlatformIntegrationFieldType;
  labelKey: string;
  helpKey?: string | null;
  required: boolean;
  /** Read back as set or not set rather than printed; the form still edits the value. */
  concealed: boolean;
  maxLength?: number | null;
  placeholder?: string | null;
  /** Where the provider posts the events this secret signs. */
  endpointUrl?: string | null;
  /** The choices a select field offers. */
  options?: { value: string; label: string }[] | null;
  /** What a select field reads as until the platform owner chooses. */
  defaultValue?: string | null;
}

export interface PlatformIntegrationTemplateSection {
  key: string;
  labelKey: string;
  descriptionKey?: string | null;
  /** Shown only for an integration holding a card product of one of these types; empty means always. */
  forSubAccountTypes: ApplicationType[];
  fields: PlatformIntegrationField[];
}

export interface PlatformIntegrationTemplate {
  category: PlatformIntegrationCategory;
  provider: string;
  /** The provider's brand name, which is never translated. */
  label: string;
  domain: string;
  descriptionKey: string;
  hasCardProducts: boolean;
  sections: PlatformIntegrationTemplateSection[];
}

export interface SavePlatformIntegrationInput {
  id?: string;
  category?: PlatformIntegrationCategory;
  provider?: string;
  name?: string;
  status?: CardProgramStatus;
  isDefault?: boolean;
  values?: PlatformIntegrationValue[];
}

export interface PlatformIntegrationCheck {
  ok: boolean;
  message?: string | null;
}

// Whether a section applies to this integration's card products.
export const sectionApplies = (
  section: PlatformIntegrationTemplateSection,
  integration: PlatformIntegration | null | undefined,
): boolean =>
  !section.forSubAccountTypes.length ||
  Boolean(
    integration?.cardProducts.some(
      (product) =>
        product.subAccountType &&
        section.forSubAccountTypes.includes(product.subAccountType),
    ),
  );

export const integrationValue = (
  integration: PlatformIntegration | null | undefined,
  key: string,
): string =>
  integration?.values.find((entry) => entry.key === key)?.value ?? "";

const usePlatformIntegrationMutations = () => {
  const [saveMutation] = useMutation(SAVE_PLATFORM_INTEGRATION);
  const [checkMutation] = useMutation(CHECK_PLATFORM_INTEGRATION);

  const saveIntegration = useCallback(
    async (input: SavePlatformIntegrationInput) => {
      const result = await saveMutation({ variables: { input } });
      return result.data?.savePlatformIntegration as
        PlatformIntegration | undefined;
    },
    [saveMutation],
  );

  // Asks the provider whether it takes these credentials. Nothing is saved.
  const checkIntegration = useCallback(
    async (input: SavePlatformIntegrationInput) => {
      const { id, category, provider, values } = input;
      const result = await checkMutation({
        variables: {
          input: id ? { id, values } : { category, provider, values },
        },
      });
      return result.data?.checkPlatformIntegration as PlatformIntegrationCheck;
    },
    [checkMutation],
  );

  return { saveIntegration, checkIntegration };
};

const usePlatformIntegrations = ({
  skip = false,
  category,
}: { skip?: boolean; category?: PlatformIntegrationCategory } = {}) => {
  const { data, loading, refetch } = useQuery(GET_PLATFORM_INTEGRATIONS, {
    variables: { category },
    fetchPolicy: "cache-and-network",
    skip,
  });
  const { saveIntegration, checkIntegration } =
    usePlatformIntegrationMutations();

  const integrations: PlatformIntegration[] = useMemo(
    () => data?.myPlatform?.platformIntegrations ?? [],
    [data],
  );

  return { integrations, loading, refetch, saveIntegration, checkIntegration };
};

export const usePlatformIntegration = (id: string) => {
  const { data, loading, refetch } = useQuery(GET_PLATFORM_INTEGRATION, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    skip: !id,
  });
  const { saveIntegration, checkIntegration } =
    usePlatformIntegrationMutations();
  const [refreshMutation, { loading: refreshing }] = useMutation(
    REFRESH_ISSUER_CARD_PRODUCTS,
  );
  const [updateCardProductMutation] = useMutation(UPDATE_ISSUER_CARD_PRODUCT);

  const integration: PlatformIntegration | null =
    data?.platformIntegration ?? null;

  const refreshCardProducts = useCallback(async () => {
    await refreshMutation({ variables: { id } });
  }, [id, refreshMutation]);

  // Rename a card program or switch it on or off. The server refuses a name
  // that is blank, too long or already used on the platform.
  const updateCardProduct = useCallback(
    async (productKey: string, input: IssuerCardProductUpdate) => {
      await updateCardProductMutation({
        variables: { id, productKey, input },
        ...(input.rewards !== undefined && {
          refetchQueries: [GET_PLATFORM_REWARDS_AVAILABLE],
        }),
      });
    },
    [id, updateCardProductMutation],
  );

  return {
    integration,
    loading,
    refetch,
    saveIntegration,
    checkIntegration,
    refreshCardProducts,
    refreshing,
    updateCardProduct,
  };
};

export const usePlatformIntegrationTemplates = () => {
  const { data, loading } = useQuery(GET_PLATFORM_INTEGRATION_TEMPLATES, {
    fetchPolicy: "cache-first",
  });

  const templates: PlatformIntegrationTemplate[] = useMemo(
    () => data?.platformIntegrationTemplates ?? [],
    [data],
  );

  const getTemplate = useCallback(
    (category?: string, provider?: string) =>
      templates.find(
        (template) =>
          template.category === category && template.provider === provider,
      ) ?? null,
    [templates],
  );

  return { templates, loading, getTemplate };
};

export default usePlatformIntegrations;
